/** The state of a mobile preview: the render on screen, what it was taken of, and the
 *  request in flight. Separate from the pane so a page can own it once and show it in
 *  more than one place — the tour record keeps it at page level and draws it in the dock
 *  or, below 1100 px of content, in a drawer, without losing the render as it moves.
 *
 *  Never renders by itself (the pane decides when): a render applies the draft through
 *  the real write funnel and rolls it back — seconds on a big tour, with row locks until
 *  the rollback — so it happens on request, never per keystroke. A `fingerprint` names
 *  what a render was taken of; the pane compares it with the current one to say "Out of
 *  date".
 */
import { useMutation } from '@tanstack/react-query';
import { useCallback, useMemo, useRef, useState } from 'react';

import {
  renderMobilePreview,
  type MobilePreviewRequest,
  type MobilePreviewResult,
  type PreviewEntity,
} from '@/api/mobilePreview';

/** What a render applies to the saved record: the editor's PATCH body (`draft` — a tour) or
 *  its changeset (`changes` and `options` — an activity, an itinerary; O4). */
export type PreviewApplied = Pick<MobilePreviewRequest, 'draft' | 'changes' | 'options'>;

export interface MobilePreviewOptions {
  entity: PreviewEntity;
  id: number;
  /** What a render applies, built only when one is requested (a compile that must not run
   *  per keystroke); undefined: nothing — the saved state. Omit for saved state only. */
  request?: () => PreviewApplied | undefined;
  /** Identity of the draft — a value, or a getter read when a render is requested. */
  fingerprint?: string | (() => string);
  /** What a render WITHOUT the draft (`runSaved`) is taken of. Default `saved`. */
  savedFingerprint?: string | (() => string);
}

export interface MobilePreviewController {
  entity: PreviewEntity;
  id: number;
  /** The render on screen (kept while the next one runs). */
  result: MobilePreviewResult | null;
  /** The fingerprint the render on screen was taken of; null: none yet. Committed when a
   *  render SUCCEEDS — a failed or locked request leaves the old render, and its "Out of
   *  date", in place. */
  renderedFp: string | null;
  pending: boolean;
  /** The last request's failure (cleared by the next request). */
  error: unknown;
  /** Render with the draft (a lazy one is built now). */
  run: () => void;
  /** Render the saved state, ignoring the draft. */
  runSaved: () => void;
  /** The draft a render on screen showed is gone (discarded, undone, saved): that render
   *  leaves the screen — the last render of the saved state comes back, else none ("Not
   *  rendered yet") — and a render of a draft still in flight is dropped when it lands. */
  dropDraft: () => void;
  /** The fingerprint the current draft would render under. */
  currentFingerprint: () => string;
}

function read(v: string | (() => string) | undefined, fallback: string): string {
  if (typeof v === 'function') return v();
  return v ?? fallback;
}

export function useMobilePreview({ entity, id, request, fingerprint, savedFingerprint }: MobilePreviewOptions): MobilePreviewController {
  // useMutation clears `data` on every mutate, which would blank the pane mid-render.
  const [shown, setShown] = useState<Shown | null>(null);
  // The last render of the saved state (what `dropDraft` brings back).
  const lastSaved = useRef<Shown | null>(null);
  // Requests are numbered; a draft render asked for at or before `droppedAt` never shows.
  const sent = useRef(0);
  const droppedAt = useRef(0);
  // The last request applied a draft (its failure is that draft's).
  const askedDraft = useRef(false);

  const latest = useRef({ entity, id, request, fingerprint, savedFingerprint });
  latest.current = { entity, id, request, fingerprint, savedFingerprint };

  // The fingerprint travels with its request and is committed with its result.
  const mutation = useMutation({
    mutationFn: ({ body, fp, n }: { body: MobilePreviewRequest; fp: string; n: number }) =>
      renderMobilePreview(body).then((result) => ({ result, fp, n })),
    onSuccess: ({ result, fp, n }) => {
      if (result.source !== 'draft') lastSaved.current = { result, fp };
      else if (n <= droppedAt.current) return;
      setShown({ result, fp });
    },
  });
  const { mutate, reset, isPending } = mutation;

  /** The request at the moment it is sent (what it applies is built now). */
  const requestNow = useCallback((withDraft: boolean): { body: MobilePreviewRequest; fp: string; n: number } => {
    const o = latest.current;
    const base = { entity: o.entity, id: o.id };
    const n = ++sent.current;
    if (!withDraft || o.request === undefined) {
      askedDraft.current = false;
      return { body: base, fp: withDraft ? currentFp(o) : read(o.savedFingerprint, 'saved'), n };
    }
    const applied = nonEmpty(o.request());
    askedDraft.current = applied.draft !== undefined || applied.changes !== undefined;
    return { body: { ...base, ...applied }, fp: currentFp(o), n };
  }, []);

  // Captured at REQUEST time (a keystroke landing mid-flight must not read as fresh) and
  // committed on success (a failure must not read as fresh either).
  const run = useCallback(() => mutate(requestNow(true)), [mutate, requestNow]);
  const runSaved = useCallback(() => mutate(requestNow(false)), [mutate, requestNow]);

  const currentFingerprint = useCallback(() => currentFp(latest.current), []);

  const pendingNow = useRef(isPending);
  pendingNow.current = isPending;
  const dropDraft = useCallback(() => {
    droppedAt.current = sent.current;
    setShown((s) => (s?.result.source === 'draft' ? lastSaved.current : s));
    // A failed render of that draft goes with it (one in flight settles on its own).
    if (askedDraft.current && !pendingNow.current) reset();
  }, [reset]);

  const result = shown?.result ?? null;
  const renderedFp = shown?.fp ?? null;
  return useMemo(
    () => ({
      entity,
      id,
      result,
      renderedFp,
      pending: isPending,
      error: mutation.error,
      run,
      runSaved,
      dropDraft,
      currentFingerprint,
    }),
    [entity, id, result, renderedFp, isPending, mutation.error, run, runSaved, dropDraft, currentFingerprint],
  );
}

/** A render on screen and the fingerprint it was taken of. */
interface Shown {
  result: MobilePreviewResult;
  fp: string;
}

/** The parts of an applied draft that carry something (an empty `draft` or `changes` is
 *  the saved state). */
function nonEmpty(applied: PreviewApplied | undefined): PreviewApplied {
  if (!applied) return {};
  const out: PreviewApplied = {};
  if (applied.draft && Object.keys(applied.draft).length > 0) out.draft = applied.draft;
  if (applied.changes && applied.changes.length > 0) {
    out.changes = applied.changes;
    if (applied.options && Object.keys(applied.options).length > 0) out.options = applied.options;
  }
  return out;
}

/** Without an explicit fingerprint: a lazy `request` cannot be fingerprinted without
 *  building it; with none, the saved state is its own JSON. */
function currentFp(o: Pick<MobilePreviewOptions, 'entity' | 'id' | 'request' | 'fingerprint'>): string {
  if (o.fingerprint !== undefined) return read(o.fingerprint, '');
  if (o.request !== undefined) return 'draft';
  return JSON.stringify({ entity: o.entity, id: o.id });
}
